/**
 * Inline editors for the two identity fields on a contact: its phone number and its country.
 *
 * The phone is the contact's identity (the unique key every message, lead and appointment hangs
 * off), so it is never a free-text box: it uses the same PhoneField as the Add dialogs, is checked
 * for format locally, and is checked against the workspace for uniqueness as the user types. The
 * server re-checks on save and the database's unique index is the final guarantee, so a stale
 * "available" can only ever turn into a clear error here, never a duplicate.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Check,
  CheckCircleOutline,
  Close,
  Edit as EditIcon,
  ErrorOutline,
  InfoOutlined,
} from '@mui/icons-material';
import { getCountries, parsePhoneNumberFromString } from 'libphonenumber-js';

import PhoneField, { formatPhoneDisplay, isValidPhone } from '@/components/ui/PhoneField';
import adminApi from '@/services/admin.api';

/** What a save failure looks like to the row: the text, plus an optional "go to the other contact". */
export interface RowSaveError {
  message: string;
  link?: { label: string; onClick: () => void };
}

const LABEL_SX = {
  fontSize: '0.6875rem',
  fontWeight: 600,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  mb: 0.25,
} as const;

function flagEmoji(code?: string): string {
  const c = String(code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c)) return '';
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65));
}

// ── Phone ────────────────────────────────────────────────────────────────────

type PhoneCheck =
  | { kind: 'idle' }
  | { kind: 'incomplete' }
  | { kind: 'checking' }
  | { kind: 'ok' }
  | { kind: 'taken'; ownerId?: string | undefined; ownerName?: string | undefined }
  | { kind: 'unverified' };

interface PhoneEditRowProps {
  contactId: string;
  /** Stored value, digits only with country code. */
  value: string;
  canEdit: boolean;
  onSave: (digits: string) => Promise<RowSaveError | null>;
  onOpenContact: (id: string) => void;
}

const CHECK_DEBOUNCE_MS = 350;

export const PhoneEditRow: React.FC<PhoneEditRowProps> = ({ contactId, value, canEdit, onSave, onOpenContact }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [check, setCheck] = useState<PhoneCheck>({ kind: 'idle' });
  const [saveError, setSaveError] = useState<RowSaveError | null>(null);
  const seq = useRef(0);

  // Format is judged locally and instantly; uniqueness is asked of the server once typing pauses.
  // The cleanup bumps `seq`, so an answer for a number the user has since typed over is ignored.
  useEffect(() => {
    if (!editing) return;
    setSaveError(null);
    if (draft === value) {
      setCheck({ kind: 'idle' });
      return;
    }
    if (!isValidPhone(draft)) {
      setCheck({ kind: 'incomplete' });
      return;
    }
    setCheck({ kind: 'checking' });
    const mine = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const r = await adminApi.checkContactPhone(draft, contactId);
        if (mine !== seq.current) return;
        if (!r.valid) setCheck({ kind: 'incomplete' });
        else if (r.available) setCheck({ kind: 'ok' });
        else setCheck({ kind: 'taken', ownerId: r.owner?.contactId, ownerName: r.owner?.contactName });
      } catch {
        if (mine === seq.current) setCheck({ kind: 'unverified' });
      }
    }, CHECK_DEBOUNCE_MS);
    return () => {
      clearTimeout(timer);
      seq.current++;
    };
  }, [draft, editing, value, contactId]);

  const start = () => {
    setDraft(value);
    setSaveError(null);
    setEditing(true);
  };

  const canSave =
    !saving && draft !== value && (check.kind === 'ok' || check.kind === 'unverified');

  const commit = async () => {
    if (!canSave) return;
    setSaving(true);
    const err = await onSave(draft);
    setSaving(false);
    if (err) setSaveError(err);
    else setEditing(false);
  };

  const takenText =
    check.kind === 'taken'
      ? check.ownerName
        ? `This number already belongs to "${check.ownerName}"`
        : 'Another contact already uses this number'
      : '';
  const errorText = saveError?.message || takenText;
  const errorLink =
    saveError?.link ??
    (check.kind === 'taken' && check.ownerId
      ? { label: 'Open contact', onClick: () => onOpenContact(check.ownerId as string) }
      : undefined);

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={LABEL_SX}>
        Phone
      </Typography>
      {editing ? (
        <Box sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PhoneField size="small" value={draft} onChange={setDraft} error={errorText} disabled={saving} />
            </Box>
            <IconButton size="small" color="primary" onClick={commit} disabled={!canSave} sx={{ mt: '2px' }}>
              {saving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ mt: '2px' }}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          {errorLink && (
            <Button size="small" onClick={errorLink.onClick} sx={{ mt: 0.25, ml: 0.5, textTransform: 'none' }}>
              {errorLink.label}
            </Button>
          )}
          {!errorText && check.kind === 'incomplete' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, ml: 1.75 }}>
              Enter the full number including its country code.
            </Typography>
          )}
          {check.kind === 'checking' && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 1.75 }}
            >
              <CircularProgress size={10} /> Checking…
            </Typography>
          )}
          {!errorText && check.kind === 'ok' && (
            <Typography
              variant="caption"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 1.75, color: 'success.main' }}
            >
              <CheckCircleOutline sx={{ fontSize: 14 }} /> Number is available
            </Typography>
          )}
          {!errorText && check.kind === 'unverified' && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, ml: 1.75 }}
            >
              <ErrorOutline sx={{ fontSize: 14 }} /> Couldn&apos;t pre-check this number — it is checked again on save.
            </Typography>
          )}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 0.75, ml: 1.75 }}
          >
            <InfoOutlined sx={{ fontSize: 14, mt: '1px' }} />
            Leads and appointments for this contact move to the new number. Message history stays.
          </Typography>
        </Box>
      ) : (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" fontWeight={500} color={value ? 'text.primary' : 'text.disabled'}>
            {formatPhoneDisplay(value) || '—'}
          </Typography>
          {canEdit && (
            <Tooltip title="Edit phone">
              <IconButton size="small" onClick={start}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
};

// ── Country ──────────────────────────────────────────────────────────────────

interface CountryOption {
  code: string;
  name: string;
}

let countryOptionsCache: CountryOption[] | null = null;
function countryOptions(): CountryOption[] {
  if (countryOptionsCache) return countryOptionsCache;
  const names =
    typeof Intl !== 'undefined' && 'DisplayNames' in Intl
      ? new Intl.DisplayNames(['en'], { type: 'region' })
      : null;
  countryOptionsCache = getCountries()
    .map((code) => ({ code, name: names?.of(code) || code }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return countryOptionsCache;
}

const countryLabel = (o: CountryOption): string => `${flagEmoji(o.code)} ${o.name} (${o.code})`.trim();

interface CountryEditRowProps {
  /** Stored ISO code, if any. */
  value?: string | undefined;
  /** A legacy free-text country name, shown only when there is no code. */
  fallbackName?: string | undefined;
  /** The contact's phone digits — used to warn when the chosen country disagrees with it. */
  phoneDigits: string;
  canEdit: boolean;
  onSave: (code: string) => Promise<RowSaveError | null>;
}

export const CountryEditRow: React.FC<CountryEditRowProps> = ({ value, fallbackName, phoneDigits, canEdit, onSave }) => {
  const options = useMemo(countryOptions, []);
  const current = useMemo(
    () => options.find((o) => o.code === String(value || '').toUpperCase()) ?? null,
    [options, value]
  );
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<CountryOption | null>(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<RowSaveError | null>(null);

  const phoneCountry = useMemo(
    () => (phoneDigits ? parsePhoneNumberFromString(`+${phoneDigits}`)?.country : undefined),
    [phoneDigits]
  );
  const mismatch = draft && phoneCountry && phoneCountry !== draft.code ? phoneCountry : null;
  const mismatchName = mismatch ? options.find((o) => o.code === mismatch)?.name || mismatch : '';

  const changed = (draft?.code ?? '') !== (current?.code ?? '');

  const start = () => {
    setDraft(current);
    setError(null);
    setEditing(true);
  };

  const commit = async () => {
    if (!changed || saving) return;
    setSaving(true);
    const err = await onSave(draft?.code ?? '');
    setSaving(false);
    if (err) setError(err);
    else setEditing(false);
  };

  return (
    <Box sx={{ py: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={LABEL_SX}>
        Country
      </Typography>
      {editing ? (
        <Box sx={{ mt: 0.5 }}>
          <Stack direction="row" spacing={0.5} alignItems="flex-start">
            <Autocomplete
              size="small"
              fullWidth
              autoHighlight
              options={options}
              value={draft}
              disabled={saving}
              onChange={(_, next) => {
                setDraft(next);
                setError(null);
              }}
              getOptionLabel={countryLabel}
              isOptionEqualToValue={(a, b) => a.code === b.code}
              renderInput={(params) => (
                <TextField
                  {...(params as any)}
                  size="small"
                  autoFocus
                  placeholder="Search country"
                  error={Boolean(error)}
                  helperText={error?.message || ' '}
                />
              )}
            />
            <IconButton size="small" color="primary" onClick={commit} disabled={!changed || saving} sx={{ mt: '3px' }}>
              {saving ? <CircularProgress size={16} /> : <Check fontSize="small" />}
            </IconButton>
            <IconButton size="small" onClick={() => setEditing(false)} disabled={saving} sx={{ mt: '3px' }}>
              <Close fontSize="small" />
            </IconButton>
          </Stack>
          {mismatch && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, ml: 1.75 }}
            >
              <InfoOutlined sx={{ fontSize: 14, mt: '1px' }} />
              This number&apos;s country code is {mismatchName}. Saving keeps {draft?.name} as the contact&apos;s country.
            </Typography>
          )}
        </Box>
      ) : (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography
            variant="body2"
            fontWeight={500}
            color={current || fallbackName ? 'text.primary' : 'text.disabled'}
          >
            {current ? countryLabel(current) : fallbackName || '—'}
          </Typography>
          {canEdit && (
            <Tooltip title="Edit country">
              <IconButton size="small" onClick={start}>
                <EditIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      )}
    </Box>
  );
};
